import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FundingApplicationService } from '@/services/funding-application.service';
import { useAuth } from '@/contexts/AuthContext';
import {
  FundingApplicationFormData,
  FundingProgram,
  FundingType,
  FundingProgramLabels,
  FundingTypeLabels,
} from '@/models/funding-application.model';
import { getFieldSuggestion } from '@/utils/funding-field-suggestions';

export const FundingApplicationForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<FundingApplicationFormData>({
    program: FundingProgram.SBA_MICROLOAN,
    fundingType: FundingType.LOAN,
    requestedAmount: 0,
    useOfFunds: '',
    operatingState: 'Minnesota',
    businessType: 'SaaS',
    incorporationState: 'Delaware',
  });

  useEffect(() => {
    if (isEditing && id) {
      loadApplication();
    }
  }, [id, isEditing]);

  const loadApplication = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const application = await FundingApplicationService.getById(id);
      if (application) {
        setFormData({
          program: application.program,
          programName: application.programName,
          fundingType: application.fundingType,
          requestedAmount: application.requestedAmount,
          useOfFunds: application.useOfFunds,
          monthlyBurn: application.monthlyBurn,
          cloudAiSubscriptions: application.cloudAiSubscriptions,
          runwayMonths: application.runwayMonths,
          earliestRevenueTrigger: application.earliestRevenueTrigger,
          operatingState: application.operatingState,
          businessType: application.businessType,
          incorporationState: application.incorporationState,
          foreignQualificationStatus: application.foreignQualificationStatus,
          ein: application.ein,
          dunsNumber: application.dunsNumber,
          yearsInBusiness: application.yearsInBusiness,
          legalStructure: application.legalStructure,
          businessAddress: application.businessAddress,
          businessPhone: application.businessPhone,
          businessWebsite: application.businessWebsite,
          currentRevenue: application.currentRevenue,
          projectedRevenue: application.projectedRevenue,
          currentExpenses: application.currentExpenses,
          bankAccountBalance: application.bankAccountBalance,
          existingDebt: application.existingDebt,
          monthlyDebtPayments: application.monthlyDebtPayments,
          creditScore: application.creditScore,
          taxReturnsFiled: application.taxReturnsFiled,
          yearsTaxReturnsFiled: application.yearsTaxReturnsFiled,
          founderName: application.founderName,
          founderTitle: application.founderTitle,
          founderOwnership: application.founderOwnership,
          founderExperience: application.founderExperience,
          personalCreditScore: application.personalCreditScore,
          personalFinancialStatement: application.personalFinancialStatement,
          executiveSummary: application.executiveSummary,
          marketAnalysis: application.marketAnalysis,
          competitiveAdvantage: application.competitiveAdvantage,
          businessModel: application.businessModel,
          targetCustomers: application.targetCustomers,
          salesStrategy: application.salesStrategy,
          repaymentPlan: application.repaymentPlan,
          revenueProjections: application.revenueProjections,
          applicationNumber: application.applicationNumber,
          lenderContactName: application.lenderContactName,
          lenderContactEmail: application.lenderContactEmail,
          lenderContactPhone: application.lenderContactPhone,
          lenderApplicationUrl: application.lenderApplicationUrl,
          notes: application.notes,
          expectedDecisionDate: application.expectedDecisionDate as Date,
          nextFollowUpDate: application.nextFollowUpDate as Date,
        });
      }
    } catch (error) {
      console.error('Error loading funding application:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile?.id) {
      alert('User not authenticated');
      return;
    }

    setSaving(true);
    try {
      if (isEditing && id) {
        await FundingApplicationService.update(id, formData, profile.id);
      } else {
        const newId = await FundingApplicationService.create(formData, profile.id);
        navigate(`/financial/funding/${newId}`);
        return;
      }
      navigate('/financial/funding');
    } catch (error) {
      console.error('Error saving funding application:', error);
      alert('Failed to save funding application. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes('Amount') || name.includes('Burn') || name.includes('Months')
        ? parseFloat(value) || 0
        : value,
    }));
  };

  const handleUseSuggestion = (fieldName: string) => {
    const suggestion = getFieldSuggestion(fieldName);
    if (suggestion) {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: suggestion.suggestion,
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading funding application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Edit Funding Application' : 'New Funding Application'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing
              ? 'Update funding application details'
              : 'Create a new funding application for FairCare operations'}
          </p>
          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-sm text-blue-800">
              <strong>📋 Important:</strong> This is an <strong>internal tracking system</strong> to help you prepare and organize 
              all the information you'll need for lender applications. When you're ready to apply to a lender (like MEDA, CRF USA, 
              SBA Microloan, etc.), you'll use the information you enter here to fill out their actual application forms. 
              This ensures you have all required information ready and organized before you start the lender's application process.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-6">
          {/* Program Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Funding Program *
            </label>
            <select
              name="program"
              value={formData.program}
              onChange={handleChange}
              required
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {Object.values(FundingProgram).map((program) => (
                <option key={program} value={program}>
                  {FundingProgramLabels[program]}
                </option>
              ))}
            </select>
          </div>

          {formData.program === FundingProgram.OTHER && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Name *
              </label>
              <input
                type="text"
                name="programName"
                value={formData.programName || ''}
                onChange={handleChange}
                required
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Enter program name"
              />
            </div>
          )}

          {/* Funding Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Funding Type *
            </label>
            <select
              name="fundingType"
              value={formData.fundingType}
              onChange={handleChange}
              required
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              {Object.values(FundingType).map((type) => (
                <option key={type} value={type}>
                  {FundingTypeLabels[type]}
                </option>
              ))}
            </select>
          </div>

          {/* Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Requested Amount ($) *
            </label>
            <input
              type="number"
              name="requestedAmount"
              value={formData.requestedAmount}
              onChange={handleChange}
              required
              min="0"
              step="0.01"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="10000"
            />
          </div>

          {/* Use of Funds */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Use of Funds *
              </label>
              <button
                type="button"
                onClick={() => handleUseSuggestion('useOfFunds')}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                💡 Use Suggestion
              </button>
            </div>
            <textarea
              name="useOfFunds"
              value={formData.useOfFunds}
              onChange={handleChange}
              required
              rows={6}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Describe how the funds will be used. Include: working capital for MVP infrastructure, cloud infrastructure (Firebase Blaze plan, AWS, etc.), AI/development tools (Cursor Ultra, ChatGPT Pro, etc.), security/compliance tooling, core SaaS subscriptions, professional services, and other operational expenses."
            />
            <p className="mt-1 text-xs text-gray-500">
              Be specific about operational costs including cloud services, AI tools, and development subscriptions that are essential for business operations. Click "Use Suggestion" to see an example based on FairCare's services.
            </p>
          </div>

          {/* Cloud/AI Subscriptions Breakdown */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cloud/AI Subscriptions Breakdown
            </label>
            <textarea
              name="cloudAiSubscriptions"
              value={formData.cloudAiSubscriptions || ''}
              onChange={handleChange}
              rows={5}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="List your cloud and AI subscription costs (e.g.,&#10;- Cursor Ultra: $X/month&#10;- ChatGPT Pro: $X/month&#10;- Firebase Blaze Plan: $X/month&#10;- Other AI/development tools: $X/month&#10;Total monthly: $X)"
            />
            <p className="mt-1 text-xs text-gray-500">
              Detail your monthly cloud and AI tool subscriptions. This helps lenders understand your operational technology costs. Include tools like Cursor Ultra, ChatGPT Pro, Firebase Blaze, and other essential development/AI subscriptions.
            </p>
          </div>

          {/* Financial Details */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Burn ($)
              </label>
              <input
                type="number"
                name="monthlyBurn"
                value={formData.monthlyBurn || ''}
                onChange={handleChange}
                min="0"
                step="0.01"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="5000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Runway (Months)
              </label>
              <input
                type="number"
                name="runwayMonths"
                value={formData.runwayMonths || ''}
                onChange={handleChange}
                min="0"
                step="1"
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="6"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Earliest Revenue Trigger
              </label>
              <input
                type="text"
                name="earliestRevenueTrigger"
                value={formData.earliestRevenueTrigger || ''}
                onChange={handleChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Pilot, employer signup, LOI, etc."
              />
            </div>
          </div>

          {/* Business Information */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Operating State *
              </label>
              <input
                type="text"
                name="operatingState"
                value={formData.operatingState}
                onChange={handleChange}
                required
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type *
              </label>
              <input
                type="text"
                name="businessType"
                value={formData.businessType}
                onChange={handleChange}
                required
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Incorporation State *
              </label>
              <input
                type="text"
                name="incorporationState"
                value={formData.incorporationState}
                onChange={handleChange}
                required
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Foreign Qualification */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Foreign Qualification Status
            </label>
            <select
              name="foreignQualificationStatus"
              value={formData.foreignQualificationStatus || ''}
              onChange={handleChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Not specified</option>
              <option value="not_needed">Not Needed</option>
              <option value="needed">Needed</option>
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Critical Lender-Required Information */}
          <div className="border-t pt-6 mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              📋 Critical Lender-Required Information
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              <strong>Note:</strong> This is internal tracking to help you prepare for lender applications. 
              Fill this out to have all your information ready when you apply to MEDA, CRF USA, SBA, etc. 
              You'll use this data to fill out their actual application forms.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>💡 How this works:</strong> This module helps you track and prepare your funding application data. 
                When you're ready to apply to a lender (like MEDA), you'll use the information you've entered here to 
                fill out their actual application form. This ensures you have all required information ready and organized.
              </p>
            </div>

            {/* Business Identification */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Business Identification (Lender-Required)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    EIN (Employer Identification Number) *
                  </label>
                  <input
                    type="text"
                    name="ein"
                    value={formData.ein || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="XX-XXXXXXX"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    <strong>Required by most lenders.</strong> Format: XX-XXXXXXX
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    DUNS Number
                  </label>
                  <input
                    type="text"
                    name="dunsNumber"
                    value={formData.dunsNumber || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="XXXXXXXXX"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    <strong>SBA-required.</strong> Get free at <a href="https://www.dnb.com/duns-number.html" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">dnb.com</a>
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years in Business
                  </label>
                  <input
                    type="number"
                    name="yearsInBusiness"
                    value={formData.yearsInBusiness || ''}
                    onChange={handleChange}
                    min="0"
                    step="0.1"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0.5"
                  />
                  <p className="mt-1 text-xs text-gray-500">Enter as decimal if less than 1 year (e.g., 0.5 for 6 months)</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Legal Structure
                  </label>
                  <select
                    name="legalStructure"
                    value={formData.legalStructure || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="llc">LLC</option>
                    <option value="corporation">Corporation (C-Corp or S-Corp)</option>
                    <option value="partnership">Partnership</option>
                    <option value="sole_proprietorship">Sole Proprietorship</option>
                    <option value="nonprofit">Nonprofit</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Financial Information (Lender-Required)</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Monthly Revenue ($)
                  </label>
                  <input
                    type="number"
                    name="currentRevenue"
                    value={formData.currentRevenue || ''}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Projected Monthly Revenue ($)
                  </label>
                  <input
                    type="number"
                    name="projectedRevenue"
                    value={formData.projectedRevenue || ''}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Bank Balance ($)
                  </label>
                  <input
                    type="number"
                    name="bankAccountBalance"
                    value={formData.bankAccountBalance || ''}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Existing Debt ($)
                  </label>
                  <input
                    type="number"
                    name="existingDebt"
                    value={formData.existingDebt || ''}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monthly Debt Payments ($)
                  </label>
                  <input
                    type="number"
                    name="monthlyDebtPayments"
                    value={formData.monthlyDebtPayments || ''}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Credit Score
                  </label>
                  <input
                    type="number"
                    name="creditScore"
                    value={formData.creditScore || ''}
                    onChange={handleChange}
                    min="300"
                    max="850"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="700"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax Returns Filed?
                  </label>
                  <select
                    name="taxReturnsFiled"
                    value={formData.taxReturnsFiled === undefined ? '' : formData.taxReturnsFiled.toString()}
                    onChange={(e) => setFormData(prev => ({ ...prev, taxReturnsFiled: e.target.value === 'true' }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Tax Returns Available
                  </label>
                  <input
                    type="number"
                    name="yearsTaxReturnsFiled"
                    value={formData.yearsTaxReturnsFiled || ''}
                    onChange={handleChange}
                    min="0"
                    max="10"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="0"
                  />
                </div>
              </div>
            </div>

            {/* Founder/Owner Information */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Founder/Owner Information (Lender-Required)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Founder/Owner Name
                  </label>
                  <input
                    type="text"
                    name="founderName"
                    value={formData.founderName || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Title
                  </label>
                  <input
                    type="text"
                    name="founderTitle"
                    value={formData.founderTitle || ''}
                    onChange={handleChange}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="CEO, Founder, etc."
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ownership Percentage (%)
                  </label>
                  <input
                    type="number"
                    name="founderOwnership"
                    value={formData.founderOwnership || ''}
                    onChange={handleChange}
                    min="0"
                    max="100"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Credit Score
                  </label>
                  <input
                    type="number"
                    name="personalCreditScore"
                    value={formData.personalCreditScore || ''}
                    onChange={handleChange}
                    min="300"
                    max="850"
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    placeholder="700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Personal Financial Statement Available?
                  </label>
                  <select
                    name="personalFinancialStatement"
                    value={formData.personalFinancialStatement === undefined ? '' : formData.personalFinancialStatement.toString()}
                    onChange={(e) => setFormData(prev => ({ ...prev, personalFinancialStatement: e.target.value === 'true' }))}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Founder Experience & Background
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('founderExperience')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="founderExperience"
                  value={formData.founderExperience || ''}
                  onChange={handleChange}
                  rows={4}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Describe your experience, background, relevant skills, and why you're qualified to run this business. Lenders want to see founder credibility."
                />
              </div>
            </div>

            {/* Business Plan Essentials */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Business Plan Essentials (Lender-Required)</h4>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Executive Summary *
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('executiveSummary')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="executiveSummary"
                  value={formData.executiveSummary || ''}
                  onChange={handleChange}
                  rows={8}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Provide a compelling executive summary (2-3 paragraphs). Include: what your business does, the problem you solve, your solution, target market, business model, and why you need funding. This is critical for lender applications."
                />
                <p className="mt-1 text-xs text-gray-500">
                  <strong>Lender Tip:</strong> This is often the first thing lenders read. Make it compelling and clear. Click "Use Suggestion" to see an example tailored to FairCare.
                </p>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Market Analysis & Opportunity
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('marketAnalysis')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="marketAnalysis"
                  value={formData.marketAnalysis || ''}
                  onChange={handleChange}
                  rows={6}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Describe your target market, market size, competition, and market opportunity. Include specific numbers if available (e.g., 'The healthcare benefits market is $X billion and growing at Y% annually')."
                />
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Competitive Advantage
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('competitiveAdvantage')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="competitiveAdvantage"
                  value={formData.competitiveAdvantage || ''}
                  onChange={handleChange}
                  rows={6}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="What makes your business unique? Why will customers choose you over competitors? What barriers to entry exist?"
                />
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Business Model
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('businessModel')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="businessModel"
                  value={formData.businessModel || ''}
                  onChange={handleChange}
                  rows={6}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="How does your business make money? Describe your revenue streams, pricing model, and customer acquisition strategy."
                />
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Target Customers
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('targetCustomers')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="targetCustomers"
                  value={formData.targetCustomers || ''}
                  onChange={handleChange}
                  rows={4}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Who are your target customers? Be specific (e.g., 'Small businesses with 10-50 employees in Minnesota seeking affordable healthcare benefits')."
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Sales & Marketing Strategy
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('salesStrategy')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="salesStrategy"
                  value={formData.salesStrategy || ''}
                  onChange={handleChange}
                  rows={5}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="How will you acquire customers? Describe your sales process, marketing channels, and customer acquisition strategy."
                />
              </div>
            </div>

            {/* Repayment Plan */}
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-3">Repayment Plan (Lender-Required)</h4>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    How Will You Repay the Loan? *
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('repaymentPlan')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="repaymentPlan"
                  value={formData.repaymentPlan || ''}
                  onChange={handleChange}
                  rows={8}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Explain in detail how you plan to repay the loan. Include: revenue projections, cash flow plans, timeline for repayment, and specific milestones that will generate revenue to service the debt."
                />
                <p className="mt-1 text-xs text-gray-500">
                  <strong>Critical for lenders:</strong> They need to see a clear, realistic plan for repayment. Click "Use Suggestion" to see an example based on FairCare's revenue model.
                </p>
              </div>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Revenue Projections (Next 12-24 Months)
                  </label>
                  <button
                    type="button"
                    onClick={() => handleUseSuggestion('revenueProjections')}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    💡 Use Suggestion
                  </button>
                </div>
                <textarea
                  name="revenueProjections"
                  value={formData.revenueProjections || ''}
                  onChange={handleChange}
                  rows={10}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  placeholder="Provide detailed revenue projections. Include monthly or quarterly projections for the next 12-24 months. Show how revenue will grow and when you'll be able to service debt payments."
                />
              </div>
            </div>
          </div>

          {/* Lender Contact */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Lender Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name
                </label>
                <input
                  type="text"
                  name="lenderContactName"
                  value={formData.lenderContactName || ''}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  name="lenderContactEmail"
                  value={formData.lenderContactEmail || ''}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  name="lenderContactPhone"
                  value={formData.lenderContactPhone || ''}
                  onChange={handleChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Application Number
              </label>
              <input
                type="text"
                name="applicationNumber"
                value={formData.applicationNumber || ''}
                onChange={handleChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="Lender's application reference"
              />
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lender Application URL
              </label>
              <input
                type="url"
                name="lenderApplicationUrl"
                value={formData.lenderApplicationUrl || ''}
                onChange={handleChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="https://lender.com/apply"
              />
              <p className="mt-1 text-xs text-gray-500">
                Link to the lender's actual application form/portal where you'll submit your application
              </p>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Decision Date
              </label>
              <input
                type="date"
                name="expectedDecisionDate"
                value={
                  formData.expectedDecisionDate
                    ? new Date(formData.expectedDecisionDate).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    expectedDecisionDate: e.target.value ? new Date(e.target.value) : undefined,
                  }))
                }
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Next Follow-up Date
              </label>
              <input
                type="date"
                name="nextFollowUpDate"
                value={
                  formData.nextFollowUpDate
                    ? new Date(formData.nextFollowUpDate).toISOString().split('T')[0]
                    : ''
                }
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    nextFollowUpDate: e.target.value ? new Date(e.target.value) : undefined,
                  }))
                }
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Internal Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes || ''}
              onChange={handleChange}
              rows={4}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              placeholder="Internal notes about this application..."
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={() => navigate('/financial/funding')}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {saving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

